/**
 * CSV Parser composable for contact import column mapping.
 * Handles file reading, delimiter detection, header extraction,
 * and header rewriting for mapped columns.
 */

/**
 * Detect CSV delimiter by counting occurrences in the first line.
 * Supports comma, semicolon, and tab.
 */
export function detectDelimiter(firstLine) {
  const semicolonCount = (firstLine.match(/;/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;
  const tabCount = (firstLine.match(/\t/g) || []).length;

  if (semicolonCount > commaCount && semicolonCount > tabCount) return ';';
  if (tabCount > commaCount && tabCount > semicolonCount) return '\t';
  return ',';
}

/**
 * Parse a single CSV line respecting quoted fields.
 * Handles fields like: "field with , comma","normal field"
 */
export function parseCsvLine(line, delimiter) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        // Check for escaped quote ""
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  fields.push(current.trim());
  return fields;
}

/**
 * Read and parse a CSV file for preview and header extraction.
 * Returns headers, first N preview rows, raw text, and detected delimiter.
 *
 * @param {File} file - The CSV file to parse
 * @param {number} previewRowCount - Number of data rows to return for preview
 * @returns {Promise<{ headers: string[], rows: string[][], rawText: string, delimiter: string }>}
 */
export async function parseCsvFile(file, previewRowCount = 5) {
  const rawText = await readFileAsText(file);

  // Strip BOM (Excel UTF-8 exports)
  const cleanText = rawText.replace(/^\uFEFF/, '');

  // Split into lines, filter empty trailing lines
  const lines = cleanText.split(/\r?\n/).filter((l, i, arr) => {
    // Keep all lines except empty trailing ones
    if (i === arr.length - 1 && l.trim() === '') return false;
    return true;
  });

  if (lines.length === 0) {
    return { headers: [], rows: [], rawText: cleanText, delimiter: ',' };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = parseCsvLine(lines[0], delimiter);

  // Parse first N data rows for preview
  const rows = [];
  const maxRows = Math.min(lines.length - 1, previewRowCount);
  for (let i = 1; i <= maxRows; i += 1) {
    if (lines[i].trim()) {
      rows.push(parseCsvLine(lines[i], delimiter));
    }
  }

  return { headers, rows, rawText: cleanText, delimiter };
}

/**
 * Rewrite CSV header line with mapped system field names.
 * Data rows are NOT modified — only the first line changes.
 * Unmapped columns get renamed to _unmapped_N so the backend ignores them.
 *
 * @param {string} rawText - Original CSV content
 * @param {Record<string, string>} mapping - { originalHeader: systemFieldKey }
 * @param {string} delimiter - Detected delimiter
 * @returns {string} Modified CSV with new header line
 */
export function rebuildCsvWithMappedHeaders(rawText, mapping, delimiter) {
  const lines = rawText.split(/\r?\n/);
  const originalHeaders = parseCsvLine(lines[0], delimiter);

  const newHeaders = originalHeaders.map((header, index) => {
    const systemField = mapping[header];
    return systemField || `_unmapped_${index}`;
  });

  lines[0] = newHeaders.join(delimiter);
  return lines.join('\n');
}

/**
 * Read a File as text using FileReader.
 */
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}
